import type { RichTextNode } from 'types';
import {
  ChecklistConditionOperator,
  ChecklistDecisionType,
  ChecklistDecisionTypeLabels,
  ChecklistEvaluationKey,
  ChecklistItemInputType,
  type ChecklistReferenceKey,
  ChecklistReferenceKeyLabels,
  FlagPurposeType,
  getEvaluationCore,
} from 'types';
import type { EvaluationParams } from 'types';

export { ChecklistConditionOperator };

export const ChecklistConditionOperatorLabels: Record<ChecklistConditionOperator, string> = {
  [ChecklistConditionOperator.Is]: 'is',
  [ChecklistConditionOperator.IsNot]: 'is not',
  [ChecklistConditionOperator.StartsWith]: 'starts with',
  [ChecklistConditionOperator.Includes]: 'contains',
  [ChecklistConditionOperator.EndsWith]: 'ends with',
};

export interface ConditionFormValue {
  name: string;
  operator: string;
  paramsRaw: string;
}

export interface FlagMappingFormValue {
  requirementFlagSettingId: string;
  criterionId?: string;
  problemId?: string;
}

export interface InputOptionFormValue {
  label: string;
  value: string;
  decisionType?: ChecklistDecisionType;
  requirementFlagSettingId?: string;
  flagMappings?: FlagMappingFormValue[];
}

export interface InputFieldFormValue {
  key: string;
  label: string;
  type: ChecklistItemInputType;
  required: boolean;
  options: InputOptionFormValue[];
  referenceKey?: ChecklistReferenceKey;
  requirementFlagSettingId?: string;
  conditions: ConditionFormValue[];
  tooltipText?: RichTextNode[];
  evaluationKey?: ChecklistEvaluationKey;
  evaluationParams?: EvaluationParams;
}

export interface DialogFormValues {
  name: string;
  description: string;
  tooltipText?: RichTextNode[];
  conditions: ConditionFormValue[];
  inputs: InputFieldFormValue[];
  documentTypes?: string[];
}

export const inputTypeOptions = [
  { value: ChecklistItemInputType.text, label: 'Text' },
  { value: ChecklistItemInputType.requirement, label: 'Requirement' },
  { value: ChecklistItemInputType.referenceValue, label: 'Reference Value' },
  { value: ChecklistItemInputType.evaluation, label: 'Evaluation' },
];

export const evaluationKeyLabels: Record<ChecklistEvaluationKey, string> = Object.fromEntries(
  Object.values(ChecklistEvaluationKey).map((key) => [key, getEvaluationCore(key)?.label ?? key]),
) as Record<ChecklistEvaluationKey, string>;

export const evaluationKeyOptions = Object.entries(evaluationKeyLabels).map(([value, label]) => ({
  value: value as ChecklistEvaluationKey,
  label,
}));

export const referenceKeyOptions = Object.entries(ChecklistReferenceKeyLabels)
  .map(([value, label]) => ({
    value: value as ChecklistReferenceKey,
    label,
  }))
  .toSorted((a, b) => a.label.localeCompare(b.label));

const SUCCESS_CHIP = 'bg-[var(--tag-container-default-lumen)] text-[var(--tag-text-default-success)]';

export const DECISION_TYPE_STYLES: Record<ChecklistDecisionType | 'none', string> = {
  none: SUCCESS_CHIP,
  approvedNoFlag: SUCCESS_CHIP,
  approved: SUCCESS_CHIP,
  [FlagPurposeType.rejection]: 'bg-[var(--tag-container-default-error)] text-[var(--tag-text-default-error)]',
  [FlagPurposeType.stipulation]: 'bg-[var(--tag-container-default-yellow)] text-[var(--tag-text-default-yellow)]',
  [FlagPurposeType.additionalReview]: 'bg-[var(--tag-container-default-volt)] text-[var(--tag-text-default-volt)]',
  [FlagPurposeType.notification]: 'bg-[var(--notification-canvas-default)] text-[var(--text-on-inverted-primary)]',
  [FlagPurposeType.changeOrder]: 'bg-[var(--tag-container-default-volt)] text-[var(--tag-text-default-volt)]',
};

export const DECISION_TYPES = Object.keys(ChecklistDecisionTypeLabels) as ChecklistDecisionType[];

export const PURPOSE_TYPE_LABELS: Record<FlagPurposeType, string> = {
  [FlagPurposeType.rejection]: 'Rejection',
  [FlagPurposeType.stipulation]: 'Stipulation',
  [FlagPurposeType.additionalReview]: 'Additional Review',
  [FlagPurposeType.notification]: 'Notification',
  [FlagPurposeType.changeOrder]: 'Change Order',
};

export const PURPOSE_TYPE_OPTIONS = Object.entries(PURPOSE_TYPE_LABELS).map(([value, label]) => ({ value, label }));

export const toCamelCase = (str: string): string => {
  return str
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
    .replace(/^[A-Z]/, (chr) => chr.toLowerCase());
};
